export type TMetaAudioRecordingConfig = {
    mimeType: string;
    extension: string;
    uploadContentType: TMetaAudioUploadContentType;
};

export type TMetaAudioUploadContentType =
    | 'audio/ogg'
    | 'audio/mp4'
    | 'audio/mpeg';

/** Only formats accepted by Meta/WhatsApp for voice messages. */
export const META_SUPPORTED_AUDIO_RECORDING_TYPES: TMetaAudioRecordingConfig[] =
    [
        {
            mimeType: 'audio/ogg;codecs=opus',
            extension: 'ogg',
            uploadContentType: 'audio/ogg',
        },
        {
            mimeType: 'audio/mp4;codecs=mp4a.40.2',
            extension: 'm4a',
            uploadContentType: 'audio/mp4',
        },
        {
            mimeType: 'audio/mp4',
            extension: 'm4a',
            uploadContentType: 'audio/mp4',
        },
    ];

export type TAudioRecorderStatus = 'idle' | 'recording' | 'paused';

export const getSupportedAudioRecordingConfig = ():
    | TMetaAudioRecordingConfig
    | null =>
    META_SUPPORTED_AUDIO_RECORDING_TYPES.find(
        ({ mimeType }) =>
            typeof MediaRecorder !== 'undefined' &&
            MediaRecorder.isTypeSupported(mimeType)
    ) || null;

export const getMetaAudioConfigFromMimeType = (
    mimeType: string
): TMetaAudioRecordingConfig | null => {
    const normalizedMimeType = mimeType.toLowerCase();

    return (
        META_SUPPORTED_AUDIO_RECORDING_TYPES.find(
            (config) =>
                normalizedMimeType === config.mimeType.toLowerCase() ||
                normalizedMimeType.startsWith(
                    `${config.uploadContentType};`
                ) ||
                normalizedMimeType === config.uploadContentType
        ) || null
    );
};

export const getMetaUploadContentType = (
    mimeType: string
): TMetaAudioUploadContentType | null => {
    const config = getMetaAudioConfigFromMimeType(mimeType);
    return config?.uploadContentType ?? null;
};

export const createAudioFileFromBlob = (blob: Blob, mimeType: string) => {
    const config = getMetaAudioConfigFromMimeType(mimeType);

    if (!config) {
        throw new Error('Errors.UnsupportedAudioFormat');
    }

    return new File(
        [blob],
        `voice-message-${Date.now()}.${config.extension}`,
        { type: config.uploadContentType }
    );
};

export const formatRecorderDuration = (durationMs: number) => {
    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

export const requestMicrophoneStream = async (): Promise<MediaStream> => {
    if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Errors.MicrophoneNotSupported');
    }

    try {
        return await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true,
            },
        });
    } catch {
        return navigator.mediaDevices.getUserMedia({ audio: true });
    }
};

export const buildWaveformLevels = (
    analyser: AnalyserNode,
    barCount = 28
): number[] => {
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);

    const chunkSize = Math.max(1, Math.floor(bufferLength / barCount));
    const levels: number[] = [];

    for (let index = 0; index < barCount; index += 1) {
        const start = index * chunkSize;
        const end = Math.min(start + chunkSize, bufferLength);
        let sum = 0;

        for (let valueIndex = start; valueIndex < end; valueIndex += 1) {
            sum += dataArray[valueIndex];
        }

        const average = sum / Math.max(1, end - start);
        levels.push(Math.max(0.12, average / 255));
    }

    return levels;
};

const mixChannelDataToMono = (audioBuffer: AudioBuffer): Float32Array => {
    if (audioBuffer.numberOfChannels === 1) {
        return audioBuffer.getChannelData(0);
    }

    const left = audioBuffer.getChannelData(0);
    const right = audioBuffer.getChannelData(1);
    const mono = new Float32Array(left.length);

    for (let index = 0; index < left.length; index += 1) {
        mono[index] = (left[index] + right[index]) / 2;
    }

    return mono;
};

const normalizeWaveformLevels = (levels: number[]) => {
    const peak = Math.max(...levels, 0.01);

    return levels.map((level) => Math.max(0.12, Math.min(1, level / peak)));
};

export const extractWaveformLevelsFromAudioBuffer = (
    audioBuffer: AudioBuffer,
    barCount = 28
): number[] => {
    const samples = mixChannelDataToMono(audioBuffer);
    const blockSize = Math.max(1, Math.floor(samples.length / barCount));
    const levels: number[] = [];

    for (let index = 0; index < barCount; index += 1) {
        const start = index * blockSize;
        const end = Math.min(start + blockSize, samples.length);
        let peak = 0;

        for (let sampleIndex = start; sampleIndex < end; sampleIndex += 1) {
            peak = Math.max(peak, Math.abs(samples[sampleIndex]));
        }

        levels.push(peak);
    }

    return normalizeWaveformLevels(levels);
};

export const extractWaveformLevelsFromUrl = async (
    url: string,
    barCount = 28
): Promise<number[]> => {
    try {
        const response = await fetch(url, {
            mode: 'cors',
            credentials: 'omit',
        });

        if (!response.ok) {
            throw new Error('Failed to fetch audio');
        }

        const arrayBuffer = await response.arrayBuffer();
        const audioContext = new AudioContext();

        try {
            const audioBuffer = await audioContext.decodeAudioData(
                arrayBuffer.slice(0)
            );

            return extractWaveformLevelsFromAudioBuffer(audioBuffer, barCount);
        } finally {
            if (audioContext.state !== 'closed') {
                await audioContext.close();
            }
        }
    } catch {
        return buildPlaceholderWaveformLevels(barCount);
    }
};

export const WAVEFORM_BAR_COUNT = 28;
export const WAVEFORM_BAR_GAP_PX = 2;
export const WAVEFORM_BAR_WIDTH_PX = 3;

export const getWaveformTrackWidth = (
    barCount = WAVEFORM_BAR_COUNT,
    barWidth = WAVEFORM_BAR_WIDTH_PX,
    gap = WAVEFORM_BAR_GAP_PX
) => barCount * barWidth + Math.max(0, barCount - 1) * gap;

export const buildPlaceholderWaveformLevels = (
    barCount = WAVEFORM_BAR_COUNT
): number[] =>
    Array.from({ length: barCount }, (_, index) => {
        const wave =
            Math.abs(Math.sin(index * 0.65) * 0.55) +
            Math.abs(Math.cos(index * 0.35) * 0.25);

        return Math.max(0.18, Math.min(1, 0.22 + wave));
    });
